import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";

const ESTADOS = ["pendiente", "confirmado", "cancelado", "completado"];
const ESTADOS_PAGO = ["pendiente", "señalado", "pagado"];

export default function CrmEventoDetalle() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [evento, setEvento] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/eventos/${id}`)
            .then((r) => setEvento(r.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, [id]);

    const update = (k, v) => setEvento((e) => ({ ...e, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                tipo: evento.tipo,
                fecha: evento.fecha || null,
                personas: evento.personas ? Number(evento.personas) : null,
                menu: evento.menu || null,
                senial: Number(evento.senial) || 0,
                total_presup: evento.total_presup ? Number(evento.total_presup) : null,
                estado_pago: evento.estado_pago,
                estado: evento.estado,
                notas: evento.notas || null,
            };
            await api.patch(`/eventos/${id}`, payload);
            toast.success("Guardado");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("¿Eliminar este evento?")) return;
        try {
            await api.delete(`/eventos/${id}`);
            toast.success("Eliminado");
            navigate("/crm/eventos");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (!evento) return <p className="text-piedra italic font-body">Cargando...</p>;

    return (
        <div className="space-y-12">
            <Link
                to="/crm/eventos"
                className="inline-flex items-center gap-2 text-piedra hover:text-dorado text-xs uppercase tracking-[0.25em] font-body"
            >
                <ArrowLeft size={14} /> Eventos
            </Link>

            <header className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                        {evento.tipo}
                    </p>
                    <h1 className="font-heading text-5xl text-crema font-light">
                        {evento.nombre}
                    </h1>
                    <p className="font-body text-piedra mt-3">
                        {evento.email || "—"} · {evento.telefono || "—"}
                    </p>
                </div>
                <button
                    onClick={remove}
                    className="text-piedra hover:text-red-400 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.2em]"
                    data-testid="evento-delete"
                >
                    <Trash2 size={14} /> Eliminar
                </button>
            </header>

            {evento.mensaje && (
                <div className="border border-dorado/20 bg-carbon-suave/40 p-6">
                    <p className="line-label">Mensaje original</p>
                    <p className="font-body text-crema italic">"{evento.mensaje}"</p>
                </div>
            )}

            <section className="grid md:grid-cols-2 gap-x-10 gap-y-8 bg-carbon-suave/40 border border-dorado/20 p-8">
                <div>
                    <label className="line-label">Tipo</label>
                    <select
                        value={evento.tipo}
                        onChange={(e) => update("tipo", e.target.value)}
                        className="input-line bg-carbon"
                    >
                        {["Boda", "Comunión", "Bautizo", "Empresa", "Otro"].map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="line-label">Fecha</label>
                    <input
                        type="date"
                        value={evento.fecha || ""}
                        onChange={(e) => update("fecha", e.target.value)}
                        className="input-line"
                    />
                </div>
                <div>
                    <label className="line-label">Personas</label>
                    <input
                        type="number"
                        value={evento.personas || ""}
                        onChange={(e) => update("personas", e.target.value)}
                        className="input-line"
                    />
                </div>
                <div>
                    <label className="line-label">Menú</label>
                    <input
                        value={evento.menu || ""}
                        onChange={(e) => update("menu", e.target.value)}
                        className="input-line"
                        placeholder="Ej: Menú degustación 65€"
                    />
                </div>
                <div>
                    <label className="line-label">Señal cobrada (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        value={evento.senial || 0}
                        onChange={(e) => update("senial", e.target.value)}
                        className="input-line"
                    />
                </div>
                <div>
                    <label className="line-label">Total presupuesto (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        value={evento.total_presup || ""}
                        onChange={(e) => update("total_presup", e.target.value)}
                        className="input-line"
                    />
                </div>
                <div>
                    <label className="line-label">Estado pago</label>
                    <select
                        value={evento.estado_pago}
                        onChange={(e) => update("estado_pago", e.target.value)}
                        className="input-line bg-carbon"
                    >
                        {ESTADOS_PAGO.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="line-label">Estado evento</label>
                    <select
                        value={evento.estado}
                        onChange={(e) => update("estado", e.target.value)}
                        className="input-line bg-carbon"
                        data-testid="evento-estado-select"
                    >
                        {ESTADOS.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="md:col-span-2">
                    <label className="line-label">Notas internas</label>
                    <textarea
                        rows={3}
                        value={evento.notas || ""}
                        onChange={(e) => update("notas", e.target.value)}
                        className="input-line resize-none"
                    />
                </div>
                <div className="md:col-span-2 text-right">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="btn-gold-solid"
                        data-testid="evento-save"
                    >
                        {saving ? "Guardando..." : "Guardar cambios"}
                    </button>
                </div>
            </section>
        </div>
    );
}
